import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowSquareOut, ChatCircle, CircleNotch } from '@phosphor-icons/react'
import type { LeadAtenderPorHashResponse, LeadDadosPorHashResponse } from '@/api/leads/lead-hash.actions'
import type { ApiErrorResponse } from '@/api/auth/auth.types'
import { LEAD_FINALIDADE_LABELS } from '@/constants/lead-finalidades'
import {
  
  
  
  atenderLeadPorHash,
  buscarDadosLeadPorHash
} from '@/api/leads/lead-hash.actions'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { getApiErrorMessage } from '@/lib/api-error'

export const Route = createFileRoute('/atender/$hash')({
  component: AtenderLeadPorHashPage,
})

function AtenderLeadPorHashPage() {
  const { hash } = Route.useParams()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [atendimento, setAtendimento] =
    useState<LeadAtenderPorHashResponse | null>(null)
  const [leadData, setLeadData] = useState<LeadDadosPorHashResponse | null>(null)
  const [hasStartedAttendance, setHasStartedAttendance] = useState(false)
  const dashboardId = leadData?.dashboardId ?? atendimento?.dashboardId

  async function handleAtender() {
    setHasStartedAttendance(true)
    setErrorMessage(null)
    setIsExpired(false)
    setIsLoading(true)

    try {
      const atendimentoResponse = await atenderLeadPorHash(hash)
      setAtendimento(atendimentoResponse)
      await queryClient.invalidateQueries({
        queryKey: ['novos-leads-count'],
      })
      await queryClient.refetchQueries({
        queryKey: ['novos-leads-count'],
        type: 'active',
      })

      const dadosResponse = await buscarDadosLeadPorHash(
        hash,
        atendimentoResponse.dadosUrl,
      )
      setLeadData(dadosResponse)
    } catch (error) {
      const apiError = error as ApiErrorResponse
      const message =
        getApiErrorMessage(apiError, 'Não foi possível processar o atendimento.')

      setErrorMessage(message)
      setIsExpired(
        apiError?.statusCode === 400 && message.toLowerCase().includes('expirado'),
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-muted/20 p-4 md:p-8">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <div
          className={`flex flex-col items-center ${
            hasStartedAttendance ? 'gap-3 pt-10' : 'min-h-[50vh] justify-center gap-4'
          }`}
        >
          <img src="/logo.svg" alt="Imobdesk" className="mb-8 h-16 w-16" />

          {!hasStartedAttendance && (
            <>
              <p className="text-center text-sm text-muted-foreground">
                Clique para visualizar os dados do lead
              </p>
              <Button
                className="w-full max-w-96"
                onClick={handleAtender}
                disabled={isLoading}
              >
                {isLoading && <CircleNotch className="mr-2 h-4 w-4 animate-spin" />}
                Atender
              </Button>
            </>
          )}

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" />
              Carregando dados do lead...
            </div>
          )}

          {errorMessage && (
            <div className="w-full max-w-md">
              <Alert variant="destructive">
                <AlertTitle>
                  {isExpired ? 'Link expirado' : 'Erro ao atender lead'}
                </AlertTitle>
                <AlertDescription>
                  {isExpired
                    ? 'Link expirado, solicite novo atendimento.'
                    : errorMessage}
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>

        {leadData && (
          <Card>
            <CardHeader>
              <CardTitle>Dados do lead</CardTitle>
            </CardHeader>

            <CardContent className="space-y-6">
              <section className="rounded-lg border p-4">
                <h3 className="mb-3 text-sm font-semibold">Contato</h3>
                <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-[160px_1fr]">
                  <p className="font-medium text-muted-foreground">Nome</p>
                  <p>{leadData.contato?.nome || '-'}</p>

                  <p className="font-medium text-muted-foreground">E-mail</p>
                  <p>{leadData.contato?.email || '-'}</p>

                  <p className="font-medium text-muted-foreground">Celular</p>
                  <div className="space-y-2">
                    <p>{formatPhone(leadData.contato?.celular ?? undefined)}</p>
                    {getWhatsAppHref(leadData.contato?.celular ?? undefined) && (
                      <Button
                        asChild
                        size="sm"
                        className="bg-[#25D366] text-white hover:bg-[#1ebe5d]"
                      >
                        <a
                          href={getWhatsAppHref(leadData.contato?.celular ?? undefined)!}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ChatCircle className="h-4 w-4" />
                          Chamar no WhatsApp
                        </a>
                      </Button>
                    )}
                  </div>

                  <p className="font-medium text-muted-foreground">Atendido em</p>
                  <p>
                    {formatDateTime(
                      (leadData.atendido ?? atendimento?.atendidoEm) ?? undefined,
                    )}
                  </p>

                  <p className="font-medium text-muted-foreground">Finalidade</p>
                  <p>{leadData.finalidade ? (LEAD_FINALIDADE_LABELS[leadData.finalidade as keyof typeof LEAD_FINALIDADE_LABELS] || leadData.finalidade) : '-'}</p>
                </div>
              </section>

              <section className="rounded-lg border p-4">
                <h3 className="mb-2 text-sm font-semibold">Imóveis de interesse</h3>
                {leadData.interesses?.imoveis?.length ? (
                  <ul className="list-disc space-y-1 pl-5 text-sm">
                    {leadData.interesses.imoveis.map((imovel) => (
                      <li key={imovel.id}>
                        #{imovel.id} - {imovel.titulo}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Nenhum imóvel de interesse.
                  </p>
                )}
              </section>

              <section className="rounded-lg border p-4">
                <h3 className="mb-2 text-sm font-semibold">Empreendimentos de interesse</h3>
                {leadData.interesses?.empreendimentos?.length ? (
                  <ul className="list-disc space-y-1 pl-5 text-sm">
                    {leadData.interesses.empreendimentos.map((empreendimento) => (
                      <li key={empreendimento.id}>
                        #{empreendimento.id} - {empreendimento.nome}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Nenhum empreendimento de interesse.
                  </p>
                )}
              </section>

              <section className="rounded-lg border p-4">
                <h3 className="mb-2 text-sm font-semibold">Anotações</h3>
                {leadData.anotacoes?.length ? (
                  <ul className="space-y-2">
                    {leadData.anotacoes.map((anotacao) => (
                      <li key={anotacao.id} className="rounded-md border p-3 text-sm">
                        <p>{anotacao.conteudo}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatDateTime(anotacao.createdAt)}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Nenhuma anotação cadastrada.
                  </p>
                )}
              </section>
            </CardContent>
          </Card>
        )}

        {leadData && dashboardId && (
          <div className="pb-4 text-center">
            <Button asChild>
              <a href={`/dashboard/${dashboardId}/leads/${leadData.leadId}`}>
                Ver na dashboard

                <ArrowSquareOut className="h-4 w-4 ml-2" />
              </a>
            </Button>

            <p className="text-xs mt-12 text-muted-foreground">ID do lead: {leadData.leadId}</p>
          </div>
        )}
      </div>
    </div>
  )
}

function formatDateTime(dateValue?: string) {
  if (!dateValue) return '-'

  const parsedDate = new Date(dateValue)
  if (Number.isNaN(parsedDate.getTime())) return dateValue

  return parsedDate.toLocaleString('pt-BR')
}

function getWhatsAppHref(phone?: string) {
  if (!phone) return null
  const digitsOnly = phone.replace(/\D/g, '')
  if (!digitsOnly) return null
  return `https://wa.me/${digitsOnly}`
}

function formatPhone(phone?: string) {
  if (!phone) return '-'
  return phone.startsWith('+') ? phone : `+${phone}`
}
